import { useState, useEffect } from 'preact/hooks'
import { apiClient, InitData, assetUrl } from '../../core'
import { t } from '../../core/i18n'

interface Instructor {
  id: string
  name: string
  bio: string | null
  profileImageUrl: string | null
  specialties: string[]
}

interface Props {
  initData: InitData
  title?: string | null
  maxItems?: number | null
  showBio?: boolean
  showSpecialties?: boolean
}

/**
 * InstructorsWidget — mirrors HS-native instructor cards.
 * Portrait card: round profile image (180px), name, specialty pills, bio.
 */
// `initData` is required by the abstract base class signature but this widget
// has no use for tenant branding — instructor cards have no booking link
// (clicking goes to a /instructors page if one exists, but apiClient already
// has tenant context, so we don't need initData here).
export function InstructorsWidget({ initData: _initData, title = null, maxItems = null, showBio = true, showSpecialties = true }: Props) {
  const [instructors, setInstructors] = useState<Instructor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetch() {
      const result = await apiClient.get<Instructor[]>('/api/widget/instructors')
      if (result.data) {
        setInstructors(result.data)
      } else if (result.error) {
        setError(result.error)
      }
      setLoading(false)
    }
    fetch()
  }, [])

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-inst-grid">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-inst-card">
              <div class="hsw-inst-img-skel" />
              <div class="hsw-skel hsw-skel-name" />
              <div class="hsw-skel hsw-skel-pills" />
              <div class="hsw-skel hsw-skel-line" />
              <div class="hsw-skel hsw-skel-line" style={{ width: '70%' }} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const visible = maxItems && maxItems > 0 ? instructors.slice(0, maxItems) : instructors

  if (!visible.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>👤</div>
        <p>{t(_initData.locale).noInstructors}</p>
      </div>
    )
  }

  return (
    <div>
      <style>{styles}</style>
      {title && <h2 class="hsw-inst-title-main">{title}</h2>}
      <div class="hsw-inst-grid">
        {visible.map(inst => (
          <div key={inst.id} class="hsw-inst-card">
            {inst.profileImageUrl ? (
              <img
                src={assetUrl(inst.profileImageUrl)}
                alt={inst.name}
                class="hsw-inst-img"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div class="hsw-inst-img hsw-inst-img-fallback" aria-hidden="true">
                {inst.name.charAt(0).toUpperCase()}
              </div>
            )}
            <h3 class="hsw-inst-name">{inst.name}</h3>
            {showSpecialties && inst.specialties.length > 0 && (
              <div class="hsw-inst-pills">
                {inst.specialties.map((s, i) => (
                  <span key={i} class="hsw-inst-pill">{s}</span>
                ))}
              </div>
            )}
            {showBio && inst.bio && (
              <p class="hsw-inst-bio">{inst.bio}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const styles = `
  .hsw-inst-title-main {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1200px;
    letter-spacing: -0.01em;
  }
  .hsw-inst-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-inst-card {
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2rem 1.5rem;
    text-align: center;
    transition: all .3s;
  }
  .hsw-inst-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-inst-img {
    display: block;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    object-fit: cover;
    margin: 0 auto 1.25rem;
  }
  .hsw-inst-img-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    font-size: 3rem;
    font-weight: 700;
  }
  .hsw-inst-name {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
  }
  .hsw-inst-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    justify-content: center;
    margin: 0.75rem 0 1rem;
  }
  .hsw-inst-pill {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 99px;
    background: rgba(255,107,53,0.1);
    color: var(--yoga-primary, #FF6B35);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .hsw-inst-bio {
    color: var(--yoga-text-muted, #525252);
    font-size: 0.9rem;
    line-height: 1.6;
    margin: 0.75rem 0 0;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* Skeletons */
  .hsw-inst-img-skel {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    margin: 0 auto 1.25rem;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
  }
  .hsw-skel {
    height: 14px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    margin: 0.5rem auto;
    border-radius: 4px;
  }
  .hsw-skel-name { width: 50%; height: 22px; margin-top: 1rem; }
  .hsw-skel-pills { width: 70%; height: 24px; margin: 1rem auto; border-radius: 12px; }
  .hsw-skel-line { width: 100%; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
